/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { TableCell } from '@/components/TableCell/TableCell';
import { TableRow } from '@/components/TableRow/TableRow';
import azCommonStyles from '@/theme/globals.module.scss';
import cx from 'classnames';
import { type ReactNode } from 'react';
import styles from './styles.module.scss';
import React from 'react';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

export type ProductSpecificationRowProps = {
  label: string;
  value: ReactNode;
  labelClassName?: string;
  valueClassName?: string;
  id?: string;
  labelTestId?: string;
  valueTestId?: string;
  compact?: boolean;
  ref?: React.RefObject<HTMLElement | null>;
};

export function ProductSpecificationRow({
  ref,
  label,
  value,
  labelClassName,
  valueClassName,
  id,
  labelTestId,
  valueTestId,
  compact = false,
}: ProductSpecificationRowProps) {
  const defaultRef = React.useRef<HTMLElement>(null);
  const finalRef = ref || defaultRef;
  const isPdpLowerV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  return (
    <TableRow
      className={isPdpLowerV2Enabled ? styles.specInnerRowV2 : styles.specInnerRow}
      id={id}
      ref={finalRef}
    >
      <TableCell
        component="th"
        scope="row"
        className={cx(
          isPdpLowerV2Enabled
            ? azCommonStyles['az-body-1-heavy']
            : azCommonStyles['az-body-1-regular'],
          styles.specsCell,
          styles.specLabel,
          labelClassName,
          {
            [styles.compactCell]: compact || isPdpLowerV2Enabled,
            [styles.specsCellV2]: isPdpLowerV2Enabled,
          },
        )}
        data-testid={labelTestId}
      >
        {label}
      </TableCell>
      <TableCell
        className={cx(
          isPdpLowerV2Enabled
            ? azCommonStyles['az-body-1-regular']
            : azCommonStyles['az-body-1-heavy'],
          styles.specsCell,
          styles.specsValue,
          valueClassName,
          {
            [styles.compactCell]: compact || isPdpLowerV2Enabled,
            [styles.specsCellV2]: isPdpLowerV2Enabled,
          },
        )}
        data-testid={valueTestId}
      >
        <span>{value}</span>
      </TableCell>
    </TableRow>
  );
}
